import { Component, Inject, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AuthTokenService as FluigAuthTokenService,
  AuthService as FluigAuthService,
  IToken,
} from '@totvs-apps-components/auth';
import { UrlHelperService } from '@totvs-apps-components/core';
import { RedirectStorageService } from 'ats-ui-lib';
import { UsageMetricsService } from '../../../app/root/metrics/usage-metrics.service';
import { LogInOrigin } from '../../../app/models/metrics.enum';
import { FluigProviderName } from '../../models/fluig-data-providers';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'ats-oauth-login',
  templateUrl: './oauth-login.component.html',
  styleUrls: ['./oauth-login.component.css'],
  standalone: false,
})
export class OAuthLoginComponent implements OnDestroy {
  private authorizationCode?: string;

  private appCode?: string;

  private forward?: string;

  private state?: string;

  private onDestroySubject = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private authTokenService: FluigAuthTokenService,
    private urlHelperService: UrlHelperService,
    @Inject(Window) private _window: Window,
    private redirectStorage: RedirectStorageService,
    private usageMetricsService: UsageMetricsService,
    private authService: FluigAuthService,
  ) {
    this.route.queryParams.pipe(takeUntil(this.onDestroySubject)).subscribe((params) => {
      this.forward = this.redirectStorage.lastValueInserted() ?? params.forward;
      this.authorizationCode = params.code;
      this.state = params.state;
      this.appCode = this.urlHelperService.getSubdomain(this._window.location.hostname);

      this.getToken(this.appCode, this.authorizationCode!);
    });

    this.authService.tokenSubject
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(async (token: IToken | null) => {
        if (token) await this.sendUsageMetric();
      });
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
  }

  private getToken(appCode: string, authorizationCode: string): void {
    sessionStorage.setItem('forward', this.forward!);
    this.authTokenService.getToken(appCode, authorizationCode, this.forward!, this.state!);
  }

  private async sendUsageMetric(): Promise<void> {
    const urlReferrer = document.referrer;
    const logInOrigin = this.getLogInOrigin(urlReferrer);
    await this.usageMetricsService.sendLoginMetricAndWait(logInOrigin);
  }

  private getLogInOrigin(urlReferrer: string): LogInOrigin {
    const logInOrigin = new Map<LogInOrigin, boolean>([
      [LogInOrigin.Google, urlReferrer.includes(FluigProviderName.Google)],
      [LogInOrigin.Linkedin, urlReferrer.includes(FluigProviderName.LinkedIn)],
      [
        LogInOrigin.Unknown,
        !urlReferrer.includes(FluigProviderName.Google) &&
          !urlReferrer.includes(FluigProviderName.LinkedIn),
      ],
    ]);

    const result = Array.from(logInOrigin).find(([_, isTrue]) => isTrue)!;
    return result[0];
  }
}
